import { useClientPortalSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSWRConfig } from "swr";
import * as Yup from "yup";
interface FormValues {
  name: string;
}
interface UseAdjudicationEditReturn {
  isEditing: boolean;
  validationSchema: Yup.ObjectSchema<FormValues>;
  handleSave: (values: FormValues) => Promise<void>;
  handleCancel: () => void;
  handleEdit: () => void;
  submitActiveStatus: (isActive: boolean) => Promise<void>;
  submitCriteria: (criteria: any[]) => Promise<void>;
  isSubmittingActiveStatus: boolean;
}

export function useAdjudicationEdit({
  mutate,
  rubricName,
  rubricId: rubricIdProp,
}: {
  mutate?: () => void;
  rubricName?: string;
  rubricId?: string;
}): UseAdjudicationEditReturn {
  const { t } = useTranslation();
  const { mutate: mutateSWR } = useSWRConfig();
  const { adjudicationId: rubricIdParam } = useParams<{
    adjudicationId: string;
  }>();
  const rubricId = rubricIdProp ?? rubricIdParam;

  const [isEditing, setIsEditing] = useState(false);
  const { submit } = useClientPortalSubmit(
    `/api/internal/client-portal/adjudication-rubrics/${rubricId}/update-name/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Adjudication rubric name updated"), { type: "success" });
        mutate?.();
      },
    }
  );

  const {
    submit: submitActiveStatusApi,
    isSubmitting: isSubmittingActiveStatus,
  } = useClientPortalSubmit(
    `/api/internal/client-portal/adjudication-rubrics/${rubricId}/update-active-status/`,
    {
      throwErrors: true,
      showToastOnError: false,
      onSuccess: () => {
        const activityLogBasePath = `/api/internal/client-portal/adjudication-rubrics/${rubricId}/activity-logs/`;
        mutateSWR((key) => {
          if (typeof key === "string") {
            return key.startsWith(activityLogBasePath);
          }
          return false;
        });
      },
    }
  );

  const { submit: submitCriteriaApi } = useClientPortalSubmit(
    `/api/internal/client-portal/adjudication-rubrics/${rubricId}/update-criteria/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Adjudication criteria updated"), { type: "success" });
        mutate?.();
      },
    }
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(t("Rubric name is required"))
      .min(1, t("Rubric name cannot be empty")),
  });

  const handleSave = async (values: FormValues) => {
    try {
      setIsEditing(false);
      await submit(values);
    } catch (error) {
      console.error("Error saving adjudication rubric:", error);
    }
  };

  const submitActiveStatus = async (isActive: boolean) => {
    try {
      await submitActiveStatusApi({ is_active: isActive });
      showToast(
        isActive
          ? t("Success. The rubric, {{ name }}, has been reactivated.", {
              name: rubricName,
            })
          : t("Success. The rubric, {{ name }}, has been deactivated.", {
              name: rubricName,
            }),
        { type: "success" }
      );
      mutate?.();
    } catch (error) {
      console.error("Error updating adjudication rubric status:", error);
      showToast(t("Failed to update rubric status"), { type: "error" });
    }
  };

  const submitCriteria = async (criteria: any[]) => {
    try {
      await submitCriteriaApi({ criteria });
    } catch (error) {
      console.error("Error updating adjudication criteria:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  return {
    isEditing,
    validationSchema,
    handleSave,
    handleCancel,
    handleEdit,
    submitActiveStatus,
    submitCriteria,
    isSubmittingActiveStatus,
  };
}
